'use client';
/* ------------------------------------------------------------------
   Appearance preferences — one object, one place that applies it.
   Mirrors gr8r's core/theme.js: every preference becomes a data-*
   attribute on <html> and the design tokens in tokens.css read them.
   ------------------------------------------------------------------ */
export const PREFS_KEY = 'ava.prefs';
export const DEFAULT_PREFS = {
  theme: 'system', // light | dark | system
  accent: 'indigo', // indigo | blue | violet | teal | rose | graphite
  side: 'comfortable', // sidebar density: comfortable | compact
  density: 'comfortable', // rows and cards: comfortable | compact
  motion: 'system', // system | reduce
};

/** Signed-in app routes follow the theme preference; landing, login and other public pages are light-only. */
const APP_ROUTE = '^/(agenda|calender|chatbot|notes|notifications|project|projects|settings|tasks)(/|$)';
export function isAppRoute(path = typeof window !== 'undefined' ? window.location.pathname : '/') {
  return new RegExp(APP_ROUTE).test(path);
}

const LEGACY = { theme: 'theme-mode', accent: 'g-accent', side: 'g-side', density: 'g-density', reduce: 'g-reduce' };

export function readPrefs() {
  if (typeof window === 'undefined') return { ...DEFAULT_PREFS };
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    if (raw) return { ...DEFAULT_PREFS, ...JSON.parse(raw) };
  } catch (_) {}
  // First run after the upgrade: pick up the keys the old screens wrote.
  const p = { ...DEFAULT_PREFS };
  try {
    const t = localStorage.getItem(LEGACY.theme);
    if (t === 'light' || t === 'dark' || t === 'system') p.theme = t;
    const a = localStorage.getItem(LEGACY.accent);
    if (a) p.accent = a;
    const s = localStorage.getItem(LEGACY.side);
    if (s) p.side = s;
    const d = localStorage.getItem(LEGACY.density);
    if (d) p.density = d;
    if (localStorage.getItem(LEGACY.reduce) === '1') p.motion = 'reduce';
  } catch (_) {}
  return p;
}

export function effectiveDark(p = readPrefs()) {
  if (p.theme === 'dark') return true;
  if (p.theme === 'light') return false;
  return typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches;
}

/** Writes the preferences onto <html>. Keep in sync with PREFS_BOOT below. */
export function applyPrefs(p = readPrefs()) {
  const r = document.documentElement;
  if (!isAppRoute()) {
    r.setAttribute('data-theme', 'light');
    r.classList.remove('dark');
    return p;
  }
  if (p.theme === 'system') r.removeAttribute('data-theme');
  else r.setAttribute('data-theme', p.theme);
  if (p.accent === 'indigo') r.removeAttribute('data-accent');
  else r.setAttribute('data-accent', p.accent);
  r.setAttribute('data-side', p.side);
  r.setAttribute('data-density', p.density);
  if (p.motion === 'reduce') r.setAttribute('data-motion', 'reduce');
  else r.removeAttribute('data-motion');
  // Legacy Tailwind screens still key off the .dark class.
  r.classList.toggle('dark', effectiveDark(p));
  return p;
}

export function savePrefs(patch) {
  const next = { ...readPrefs(), ...patch };
  try {
    localStorage.setItem(PREFS_KEY, JSON.stringify(next));
    localStorage.setItem(LEGACY.theme, next.theme); // the old settings screen still reads this
  } catch (_) {}
  applyPrefs(next);
  return next;
}

/**
 * Same logic as applyPrefs, as a plain script for <head>, so the first paint already has the
 * right theme and no flash happens before React hydrates.
 */
export const PREFS_BOOT = `(function(){try{var d=${JSON.stringify(DEFAULT_PREFS)};var p=null;try{p=JSON.parse(localStorage.getItem('${PREFS_KEY}')||'null')}catch(e){}
if(!p){p={};var t=localStorage.getItem('theme-mode');if(t==='light'||t==='dark'||t==='system')p.theme=t;var a=localStorage.getItem('g-accent');if(a)p.accent=a;var s=localStorage.getItem('g-side');if(s)p.side=s;var n=localStorage.getItem('g-density');if(n)p.density=n;if(localStorage.getItem('g-reduce')==='1')p.motion='reduce';}
for(var k in d){if(p[k]==null)p[k]=d[k]}var r=document.documentElement;
if(!new RegExp('${APP_ROUTE}').test(location.pathname)){r.setAttribute('data-theme','light');r.classList.remove('dark');return;}
if(p.theme==='system')r.removeAttribute('data-theme');else r.setAttribute('data-theme',p.theme);
if(p.accent==='indigo')r.removeAttribute('data-accent');else r.setAttribute('data-accent',p.accent);
r.setAttribute('data-side',p.side);r.setAttribute('data-density',p.density);
if(p.motion==='reduce')r.setAttribute('data-motion','reduce');else r.removeAttribute('data-motion');
var dark=p.theme==='dark'||(p.theme==='system'&&matchMedia('(prefers-color-scheme: dark)').matches);r.classList.toggle('dark',dark);}catch(e){}})();`;
